import i18n from '../i18n';
import {
  formatBillingInterval as formatBillingIntervalShared,
  formatCapabilityCode as formatCapabilityCodeShared,
  formatModuleCode as formatModuleCodeShared,
  formatModuleLabel,
  formatModuleSource,
  formatProvisioningStatus as formatProvisioningStatusShared,
  formatSchemaStatus as formatSchemaStatusShared,
  formatTenantStatus as formatTenantStatusShared,
  formatAccessGateStatus,
  humanizeCode,
} from './customerPortalLabels';

/**
 * Never show raw i18n keys in UI. Prefer explicit fallback / humanized text.
 */
export function safeTranslate(tOrKey, keyOrFallback, fallback) {
  // Overload: safeTranslate(t, key, fallback) or safeTranslate(key, fallback)
  let t = i18n.t.bind(i18n);
  let key = tOrKey;
  let fb = keyOrFallback;

  if (typeof tOrKey === 'function') {
    t = tOrKey;
    key = keyOrFallback;
    fb = fallback;
  }

  if (!key) {
    return fb || '';
  }

  const value = t(key, { defaultValue: '' });
  if (!value || value === key || looksLikeI18nKey(value)) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[systemLabels] Missing i18n key:', key);
    }
    return fb || humanizeCode(String(key).split('.').pop());
  }

  return value;
}

export function looksLikeI18nKey(value) {
  if (!value || typeof value !== 'string') {
    return false;
  }
  if (value.includes(' ') && !value.includes('_')) {
    return false;
  }
  return /^(steps|warnings|statuses|tenantProvisioning|setupWizard|launchReadiness|systemLabels)\.[\w.]+$/i.test(value)
    || /^[\w]+:[\w.]+$/.test(value)
    || /^[\w]+\.[\w]+\.[\w]+$/.test(value);
}

function warnUnknown(kind, value) {
  if (process.env.NODE_ENV === 'development' && value) {
    // eslint-disable-next-line no-console
    console.warn(`[systemLabels] Unknown ${kind}:`, value);
  }
}

function quietTranslate(...keys) {
  for (const key of keys) {
    if (key && i18n.exists(key)) {
      return i18n.t(key);
    }
  }
  return null;
}

function fromNamespace(ns, path, value, extraFallback) {
  const normalized = String(value || '').toLowerCase().replace(/-/g, '_');
  if (!normalized) {
    return '';
  }
  const key = `${ns}:${path}.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  warnUnknown(path, value);
  return extraFallback || humanizeCode(value);
}

/**
 * Generic status/code formatter — tries common namespaces without console noise.
 */
export function formatStatusCode(value) {
  const normalized = String(value || '').toLowerCase().replace(/-/g, '_');
  if (!normalized) {
    return '';
  }
  return quietTranslate(
    `statuses:subscription.${normalized}`,
    `statuses:invoice.${normalized}`,
    `statuses:payment.${normalized}`,
    `statuses:provisioning_status.${normalized}`,
    `statuses:provisioning_run.${normalized}`,
    `statuses:provisioning_step.${normalized}`,
    `statuses:health_check.${normalized}`,
    `statuses:check.${normalized}`,
    `statuses:readiness.${normalized}`,
    `statuses:tenant.${normalized}`,
    `statuses:tenant_access.${normalized}`,
    `customerPortal:provisioningStatus.${normalized}`,
    `customerPortal:paymentStatus.${normalized}`,
    `customerPortal:accessStatus.${normalized}`,
    `customerPortal:schemaStatus.${normalized}`,
    `systemLabels:provisioningModes.${normalized}`,
    `systemLabels:billingStatus.${normalized}`,
    `systemLabels:featureTypes.${normalized}`,
    `systemLabels:seedScopes.${normalized}`,
  ) || '';
}

export function formatSeedScope(value) {
  const normalized = String(value || '').toLowerCase().replace(/-/g, '_');
  if (!normalized) {
    return '';
  }
  return quietTranslate(
    `systemLabels:seedScopes.${normalized}`,
    `systemLabels:featureTypes.${normalized}`,
    `setupProfiles:seedScopes.${normalized}`,
  ) || humanizeCode(value);
}

export function formatTenantStatus(value) {
  return formatTenantStatusShared(value) || fromNamespace('statuses', 'tenant', value);
}

export function formatTenantAccessStatus(value) {
  return formatAccessGateStatus(value) || fromNamespace('statuses', 'tenant_access', value);
}

export function formatAccessResult(value) {
  return fromNamespace('systemLabels', 'accessResults', value)
    || formatTenantAccessStatus(value);
}

export function formatBillingInterval(value) {
  return formatBillingIntervalShared(value);
}

export function formatBillingStatus(value) {
  const normalized = String(value || '').toLowerCase();
  if (!normalized) {
    return '';
  }
  const found = quietTranslate(
    `statuses:invoice.${normalized}`,
    `statuses:payment.${normalized}`,
    `statuses:subscription.${normalized}`,
    `customerPortal:paymentStatus.${normalized}`,
    `systemLabels:billingStatus.${normalized}`,
  );
  if (found) {
    return found;
  }
  // Non-billing statuses (record_only, warning, …) must not warn as billingStatus.
  return '';
}

export function formatProvisioningMode(value) {
  return fromNamespace('systemLabels', 'provisioningModes', value);
}

export function formatProvisioningStatus(value) {
  return formatProvisioningStatusShared(value)
    || fromNamespace('statuses', 'provisioning_status', value)
    || fromNamespace('statuses', 'provisioning_run', value);
}

export function formatSchemaStatus(value) {
  return formatSchemaStatusShared(value)
    || fromNamespace('statuses', 'schema_plan', value);
}

export function formatReadinessStatus(value) {
  return fromNamespace('statuses', 'readiness', value)
    || fromNamespace('statuses', 'check', value);
}

export function formatLaunchStatus(value) {
  const normalized = String(value || '').toLowerCase();
  const map = {
    go: 'launchReadiness:statusGo',
    go_with_warnings: 'launchReadiness:statusGoWithWarnings',
    no_go: 'launchReadiness:statusNoGo',
  };
  if (map[normalized] && i18n.exists(map[normalized])) {
    return i18n.t(map[normalized]);
  }
  return fromNamespace('systemLabels', 'launchStatus', value);
}

export function formatBoolean(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return i18n.t('statuses:yes');
  }
  if (value === false || value === 'false' || value === 0 || value === '0') {
    return i18n.t('statuses:no');
  }
  return value == null ? '' : String(value);
}

export function formatModuleCode(value) {
  return formatModuleCodeShared(value);
}

export function formatCapabilityCode(value) {
  const normalized = String(value || '').toLowerCase().replace(/-/g, '_');
  if (!normalized) {
    return '';
  }
  const found = quietTranslate(
    `systemLabels:capabilities.${normalized}`,
    `setupProfiles:items.${normalized}`,
  );
  if (found) {
    return found;
  }
  return formatCapabilityCodeShared(value);
}

export function formatSetupProfileCode(value) {
  return fromNamespace('systemLabels', 'setupProfiles', value);
}

export function formatSetupWarning(value) {
  if (!value) {
    return '';
  }
  let code = String(value);
  if (code.startsWith('warnings.')) {
    code = code.slice('warnings.'.length);
  }
  const key = `setupWizard:warnings.${code}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  const systemKey = `systemLabels:setupWarnings.${code}`;
  if (i18n.exists(systemKey)) {
    return i18n.t(systemKey);
  }

  // Seeded / legacy free-text English warnings → localized keys
  const phraseMap = [
    {
      re: /MafertApp create.*MafertSystem handling.*dual-flow/i,
      key: 'systemLabels:setupWarnings.future_dual_flow',
    },
  ];
  for (const phrase of phraseMap) {
    if (phrase.re.test(code) && i18n.exists(phrase.key)) {
      return i18n.t(phrase.key);
    }
  }

  // Free-text warnings: show as-is without treating spaces as unknown codes
  if (/\s/.test(code)) {
    return code;
  }

  warnUnknown('setupWarning', value);
  return humanizeCode(code);
}

export function formatSource(value) {
  return formatModuleSource(value) || humanizeCode(value);
}

export function resolveLaunchReadinessI18nKey(rawKey) {
  if (!rawKey || typeof rawKey !== 'string') {
    return null;
  }
  if (rawKey.includes(':')) {
    return rawKey;
  }
  if (rawKey.startsWith('launchReadiness.')) {
    return `launchReadiness:${rawKey.slice('launchReadiness.'.length)}`;
  }
  if (rawKey.startsWith('systemLabels.')) {
    return `systemLabels:${rawKey.slice('systemLabels.'.length)}`;
  }
  if (rawKey.startsWith('permissionCleanup.')) {
    return `permissionCleanup:${rawKey.slice('permissionCleanup.'.length)}`;
  }
  return rawKey;
}

/** Auto-generated remediation keys → existing curated remediation strings */
const REMEDIATION_KEY_ALIASES = {
  tenantDatabaseConnectionVerified: 'runVerifyConnection',
  tenantDatabaseRecordExists: 'createTenantDatabaseRecord',
  tenantDatabaseNameValid: 'regenerateTenantDatabaseName',
  databaseNameValid: 'regenerateTenantDatabaseName',
  tenantDatabaseCreatedOrDryRun: 'runProvisioningCreateDatabase',
  schemaArtifactExists: 'runMigrationAudit',
  schemaPlanExists: 'buildSchemaPlan',
  schemaPlanValid: 'resolveSchemaPlanBlockers',
  schemaPlanApproved: 'approveSchemaPlan',
  tenantSchemaMigrationsApplied: 'enableSchemaRunner',
  missingRequiredSetupItems: 'completeSetupWizard',
  setupCompletionTrackingDeferred: 'useSetupWizardTracking',
  schemaArtifactHashesMatch: 'runMigrationAudit',
  schemaRunnerConfigValid: 'enableSchemaRunner',
  migrationRunnerEnabledOrDryRun: 'enableSchemaRunner',
  schemaRunnerEnabledOrDryRun: 'enableSchemaRunner',
};

function humanizeCamelCase(value) {
  return String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

export function translateLaunchReadinessRemediation(rawKey) {
  if (!rawKey) {
    return null;
  }
  const text = String(rawKey).trim();
  const resolved = resolveLaunchReadinessI18nKey(text);
  if (resolved && i18n.exists(resolved)) {
    return i18n.t(resolved);
  }

  const last = text.includes('.') ? text.split('.').pop() : text;
  const direct = `launchReadiness:remediations.${last}`;
  if (i18n.exists(direct)) {
    return i18n.t(direct);
  }

  const alias = REMEDIATION_KEY_ALIASES[last];
  if (alias) {
    const aliasKey = `launchReadiness:remediations.${alias}`;
    if (i18n.exists(aliasKey)) {
      return i18n.t(aliasKey);
    }
  }

  return null;
}

/**
 * Safe display for unknown enums / codes. Never show raw i18n-like keys as primary UI text.
 */
export function formatUnknownCode(value) {
  if (value == null || value === '') {
    return i18n.t('common:empty_value', { defaultValue: '—' });
  }

  const text = String(value).trim();
  if (!text) {
    return i18n.t('common:empty_value', { defaultValue: '—' });
  }

  if (looksLikeI18nKey(text)) {
    const resolved = resolveLaunchReadinessI18nKey(text);
    if (resolved && i18n.exists(resolved)) {
      return i18n.t(resolved);
    }
    warnUnknown('i18nKey', text);
    return humanizeCode(text.split('.').pop());
  }

  if (/^[a-z0-9]+(?:[_-][a-z0-9]+)+$/i.test(text)) {
    const normalized = text.toLowerCase().replace(/-/g, '_');
    const known = quietTranslate(
      `tenants:events.sources.${normalized}`,
      `tenants:events.types.${normalized}`,
      `systemLabels:featureTypes.${normalized}`,
      `systemLabels:seedScopes.${normalized}`,
      `systemLabels:capabilities.${normalized}`,
      `systemLabels:setupProfiles.${normalized}`,
      `setupProfiles:items.${normalized}`,
      `statuses:severity.${normalized}`,
    ) || formatStatusCode(normalized);
    if (known) {
      return known;
    }
    warnUnknown('code', text);
    return humanizeCode(text);
  }

  return text;
}

export function translateLaunchReadinessKey(rawKey, params = {}) {
  const key = resolveLaunchReadinessI18nKey(rawKey);
  if (!key) {
    return null;
  }
  if (i18n.exists(key)) {
    return i18n.t(key, params);
  }
  return null;
}

/**
 * Localize admin Go/No-Go and readiness English backend messages.
 * Prefer message_key from API; fall back to English pattern map.
 */
export function formatReadinessMessage(valueOrCheck, maybeParams) {
  if (!valueOrCheck) {
    return '';
  }

  if (typeof valueOrCheck === 'object') {
    const metadata = (valueOrCheck.metadata && typeof valueOrCheck.metadata === 'object')
      ? valueOrCheck.metadata
      : {};
    const params = {
      ...metadata,
      ...(valueOrCheck.message_params || {}),
      ...(metadata.message_params || {}),
    };
    if (params.status != null) {
      params.status = formatStatusCode(params.status) || String(params.status);
    }
    if (params.latest_run_status != null) {
      params.latest_run_status = formatStatusCode(params.latest_run_status) || String(params.latest_run_status);
      if (params.status == null || params.status === params.latest_run_status) {
        params.status = params.latest_run_status;
      }
    }
    // Recover count from English message when older reports omit message_params.
    if (params.count == null && valueOrCheck.message) {
      const countMatch = String(valueOrCheck.message).match(
        /^(\d+)\s+(?:enabled module subscription|setup profile)/i,
      ) || String(valueOrCheck.message).match(
        /Tenant has (\d+) enabled module/i,
      );
      if (countMatch) {
        params.count = countMatch[1];
      }
    }
    if (params.access_status != null) {
      params.access_status = formatTenantAccessStatus(params.access_status) || params.access_status;
    }
    if (params.subscription_id != null && params.id == null) {
      params.id = params.subscription_id;
    }
    if (params.database == null && params.database_name != null) {
      params.database = params.database_name;
    }
    const fromKey = translateLaunchReadinessKey(
      valueOrCheck.message_key || metadata.message_key,
      params,
    );
    if (fromKey) {
      return fromKey;
    }
    const fromRemediation = translateLaunchReadinessRemediation(
      valueOrCheck.message_key || metadata.message_key || valueOrCheck.remediation_key,
    );
    if (fromRemediation) {
      return fromRemediation;
    }
    return formatReadinessMessage(valueOrCheck.message || '', params);
  }

  const text = String(valueOrCheck).trim();
  if (looksLikeI18nKey(text) || /\.remediations\./i.test(text)) {
    const translated = translateLaunchReadinessKey(text, maybeParams || {})
      || translateLaunchReadinessRemediation(text);
    if (translated) {
      return translated;
    }
    const last = text.split('.').pop();
    return humanizeCamelCase(last);
  }

  const patterns = [
    {
      re: /^Missing mappings for:\s*(.+)$/i,
      key: 'launchReadiness:messages.missingMappings',
      map: (m) => ({ modules: formatModuleList(m[1]) }),
    },
    {
      re: /^Missing setup profiles for:\s*(.+)$/i,
      key: 'launchReadiness:messages.missingSetupProfiles',
      map: (m) => ({ modules: formatModuleList(m[1]) }),
    },
    {
      re: /^All enabled tenant modules have MafertApp mappings\.?$/i,
      key: 'launchReadiness:messages.selectedModulesHaveMappings',
    },
    {
      re: /^All enabled tenant modules have setup profiles\.?$/i,
      key: 'launchReadiness:messages.selectedModulesHaveSetupProfiles',
    },
    {
      re: /^Trial period is active\.?$/i,
      key: 'launchReadiness:messages.trialPeriodActive',
    },
    {
      re: /^Tenant database is record_only/i,
      key: 'launchReadiness:messages.tenantDatabaseRecordOnly',
    },
    {
      re: /^Tenant database is record-only\.?$/i,
      key: 'launchReadiness:messages.tenantDatabaseRecordOnly',
    },
    {
      re: /^Tenant database is record-only or pending provisioning\.?$/i,
      key: 'launchReadiness:messages.tenantDatabasePendingProvisioning',
    },
    {
      re: /^Tenant database is provisioned and connection is OK\.?$/i,
      key: 'systemLabels:readinessMessages.databaseProvisioned',
    },
    {
      re: /^Tenant database provisioning previously failed\.?$/i,
      key: 'systemLabels:readinessMessages.databaseProvisioningFailed',
    },
    {
      re: /^Required setup steps are incomplete:\s*(.+)$/i,
      key: 'launchReadiness:messages.requiredSetupStepsIncomplete',
      map: (m) => ({ steps: formatSetupStepList(m[1]) }),
    },
    {
      re: /^Setup wizard launch criteria not met yet\.?$/i,
      key: 'launchReadiness:messages.setupWizardLaunchCriteriaNotMet',
    },
    {
      re: /^Tenant migration runner is disabled/i,
      key: 'launchReadiness:messages.migrationRunnerDisabled',
    },
    {
      re: /^Tenant database metadata record exists\.?$/i,
      key: 'launchReadiness:messages.tenantDatabaseRecordExists.passed',
    },
    {
      re: /^No tenant_databases record found\.?$/i,
      key: 'launchReadiness:messages.tenantDatabaseRecordExists.failed',
    },
    {
      re: /^Database name '(.+)' passes validation\.?$/i,
      key: 'launchReadiness:messages.databaseNamePassesValidation',
      map: (m) => ({ database: m[1] }),
    },
    {
      re: /^Tenant database name missing or invalid\.?$/i,
      key: 'launchReadiness:messages.databaseNameValid.failed',
    },
    {
      re: /^Tenant database provisioned\.?$/i,
      key: 'launchReadiness:messages.tenantDatabaseCreatedOrDryRun.passed',
    },
    {
      re: /^Dry-run mode — physical DB creation deferred\.?$/i,
      key: 'launchReadiness:messages.dryRunPhysicalDbDeferred',
    },
    {
      re: /^Tenant database not yet provisioned\.?$/i,
      key: 'launchReadiness:messages.tenantDatabaseCreatedOrDryRun.failed',
    },
    {
      re: /^DB creation disabled — dry-run expected\.?$/i,
      key: 'launchReadiness:messages.dbCreationDisabledDryRunExpected',
    },
    {
      re: /^Database connection verified successfully\.?$/i,
      key: 'launchReadiness:messages.databaseConnectionVerifiedSuccessfully',
    },
    {
      re: /^Database name valid$/i,
      key: 'launchReadiness:checks.databaseNameValid',
    },
    {
      re: /^Tenant database created or dry-run$/i,
      key: 'launchReadiness:checks.tenantDatabaseCreatedOrDryRun',
    },
    {
      re: /^Tenant database connection verified$/i,
      key: 'launchReadiness:checks.tenantDatabaseConnectionVerified',
    },
    {
      re: /^Tenant database record exists$/i,
      key: 'launchReadiness:checks.tenantDatabaseRecordExists',
    },
    {
      re: /^Tenant database name valid$/i,
      key: 'launchReadiness:checks.tenantDatabaseNameValid',
    },
    {
      re: /^Create tenant database record during onboarding\.?$/i,
      key: 'launchReadiness:remediations.createTenantDatabaseRecord',
    },
    {
      re: /^Regenerate tenant database name via provisioning plan\.?$/i,
      key: 'launchReadiness:remediations.regenerateTenantDatabaseName',
    },
    {
      re: /^Run provisioning with create_database mode when enabled\.?$/i,
      key: 'launchReadiness:remediations.runProvisioningCreateDatabase',
    },
    {
      re: /^Tenant seed runner is disabled\.?$/i,
      key: 'launchReadiness:messages.seedRunnerDisabled',
    },
    {
      re: /^No schema version registry entry/i,
      key: 'launchReadiness:messages.noSchemaVersionRegistry',
    },
    {
      re: /^Schema plan not created\.?$/i,
      key: 'launchReadiness:messages.schemaPlanNotCreated',
    },
    {
      re: /^No schema plan to review\.?$/i,
      key: 'launchReadiness:messages.noSchemaPlanToReview',
    },
    {
      re: /^Real MafertApp seed apply is deferred/i,
      key: 'launchReadiness:messages.mafertappSeedApplyDeferred',
    },
    {
      re: /^Real MafertApp permission cleanup apply is deferred/i,
      key: 'launchReadiness:messages.permissionCleanupApplyDeferred',
    },
    {
      re: /^Support module not active\.?$/i,
      key: 'launchReadiness:messages.supportModuleNotActive',
    },
    {
      re: /^Support module active/i,
      key: 'launchReadiness:messages.supportDualFlowActive',
    },
    {
      re: /^No tenant schema plan found\.?$/i,
      key: 'launchReadiness:messages.noSchemaPlan',
    },
    {
      re: /^No tenant schema plan exists yet\.?$/i,
      key: 'launchReadiness:messages.noSchemaPlan',
    },
    {
      re: /^Schema plan has blockers or invalid status\.?$/i,
      key: 'launchReadiness:messages.schemaPlanBlockers',
    },
    {
      re: /^Schema plan not approved\.?$/i,
      key: 'launchReadiness:messages.schemaPlanNotApproved',
    },
    {
      re: /^Database connection not verified yet\.?$/i,
      key: 'launchReadiness:messages.databaseConnectionNotVerified',
    },
    {
      re: /^Test seed disabled — informational only\.?$/i,
      key: 'launchReadiness:messages.testSeedDisabled',
    },
    {
      re: /^No schema artifacts registered yet\.?$/i,
      key: 'launchReadiness:messages.noSchemaArtifacts',
    },
    {
      re: /^Schema migrations not applied yet\.?$/i,
      key: 'launchReadiness:messages.schemaMigrationsPending',
    },
    {
      re: /^Real-time push provider check deferred/i,
      key: 'launchReadiness:messages.pushProviderDeferred',
    },
    {
      re: /^(\d+)\s+required setup item group\(s\) documented as missing\.?$/i,
      key: 'launchReadiness:messages.missingSetupItemGroups',
      map: (m) => ({ count: m[1] }),
    },
    {
      re: /^Real setup data write and completion tracking deferred/i,
      key: 'launchReadiness:messages.setupTrackingDeferred',
    },
    {
      re: /^SMS-relevant modules enabled but internal SMS reporting endpoint disabled\.?$/i,
      key: 'launchReadiness:messages.smsEndpointDisabled',
    },
    {
      re: /^Add module mapping for enabled modules\.?$/i,
      key: 'launchReadiness:remediations.addModuleMapping',
    },
    // --- Go/No-Go English fallbacks (12F.6g.8 runtime audit) ---
    {
      re: /^Create a trial or paid subscription before launch\.?$/i,
      key: 'launchReadiness:remediations.subscriptionExists',
    },
    {
      re: /^Subscription access status is accessible \(trial or paid\)\.?$/i,
      key: 'launchReadiness:messages.trialOrPaidActive.passed',
    },
    {
      re: /^Tenant has (\d+) enabled module subscription\(s\)\.?$/i,
      key: 'launchReadiness:messages.activeModulesExist.passed',
      map: (m) => ({ count: m[1] }),
    },
    {
      re: /^Select at least one paid module for production tenants\.?$/i,
      key: 'launchReadiness:remediations.activeModulesExist',
    },
    {
      re: /^No pending unpaid upgrade blocking launch\.?$/i,
      key: 'launchReadiness:messages.noBlockingPendingPayment.passed',
    },
    {
      re: /^Complete payment for pending upgrade or cancel the change request\.?$/i,
      key: 'launchReadiness:remediations.noBlockingPendingPayment',
    },
    {
      re: /^No scheduled downgrade pending\.?$/i,
      key: 'launchReadiness:messages.scheduledDowngradeNotBlocking.passed',
    },
    {
      re: /^Review scheduled downgrade before launch\.?$/i,
      key: 'launchReadiness:remediations.scheduledDowngradeNotBlocking',
    },
    {
      re: /^Tenant access not blocked$/i,
      key: 'launchReadiness:checks.tenantAccessNotBlocked',
    },
    {
      re: /^Payment gate does not block tenant access\.?$/i,
      key: 'launchReadiness:messages.paymentGateStatusOk.passed',
    },
    {
      re: /^Set subscription period end dates for billing clarity\.?$/i,
      key: 'launchReadiness:remediations.subscriptionPeriodDatesSet',
    },
    {
      re: /^Provisioning config enabled or dry-run$/i,
      key: 'launchReadiness:checks.provisioningConfigEnabledOrDryRun',
    },
    {
      re: /^Provisioning in dry-run mode — DB creation not required\.?$/i,
      key: 'launchReadiness:messages.provisioningConfigEnabledOrDryRun.passed',
    },
    {
      re: /^Run provisioning with create_database mode when enabled\.?$/i,
      key: 'launchReadiness:remediations.provisioningConfigEnabledOrDryRun',
    },
    {
      re: /^Latest provisioning run status:\s*(.+)\.?$/i,
      key: 'launchReadiness:messages.latestProvisioningRunSuccess.passed',
      map: (m) => ({ status: formatStatusCode(m[1].replace(/\.$/, '')) || m[1].replace(/\.$/, '') }),
    },
    {
      re: /^Migration runner enabled or dry-run$/i,
      key: 'launchReadiness:checks.migrationRunnerEnabledOrDryRun',
    },
    {
      re: /^Schema runner disabled — dry-run\/validate only\.?$/i,
      key: 'launchReadiness:messages.migrationRunnerEnabledOrDryRun.passed',
    },
    {
      re: /^Enable SYSTEM_TENANT_SCHEMA_RUNNER_ENABLED for apply\.?$/i,
      key: 'launchReadiness:remediations.migrationRunnerEnabledOrDryRun',
    },
    {
      re: /^Tenant schema artifacts enabled\.?$/i,
      key: 'launchReadiness:messages.schemaRunnerConfigValid.passed',
    },
    {
      re: /^Schema runner enabled or dry-run$/i,
      key: 'launchReadiness:checks.schemaRunnerEnabledOrDryRun',
    },
    {
      re: /^Schema runner apply disabled — dry-run expected\.?$/i,
      key: 'launchReadiness:messages.schemaRunnerEnabledOrDryRun.passed',
    },
    {
      re: /^Re-run MafertApp migration audit and sync artifacts\.?$/i,
      key: 'launchReadiness:remediations.schemaArtifactHashesMatch',
    },
    {
      re: /^Storage provider health OK or not checked\.?$/i,
      key: 'launchReadiness:messages.storageProviderHealthOk.passed',
    },
    {
      re: /^Run provider health check for storage\.?$/i,
      key: 'launchReadiness:remediations.storageProviderHealthOk',
    },
    {
      re: /^global_settings_baseline setup profile is defined\.?$/i,
      key: 'launchReadiness:messages.globalSetupProfileExists.passed',
    },
    {
      re: /^Enabled module setup profiles exist$/i,
      key: 'launchReadiness:checks.enabledModuleSetupProfilesExist',
    },
    {
      re: /^(\d+) setup profile\(s\) apply to this tenant\.?$/i,
      key: 'launchReadiness:messages.enabledModuleSetupProfilesExist.passed',
      map: (m) => ({ count: m[1] }),
    },
    {
      re: /^Enable paid modules and verify setup profiles\.?$/i,
      key: 'launchReadiness:remediations.enabledModuleSetupProfilesExist',
    },
    {
      re: /^Setup requirements blueprint is available \(completion tracking deferred\)\.?$/i,
      key: 'launchReadiness:messages.setupRequirementsKnown.passed',
    },
    {
      re: /^Production demo data disabled$/i,
      key: 'launchReadiness:checks.productionDemoDataDisabled',
    },
    {
      re: /^Production demo data seeding disabled by default \(blueprint only\)\.?$/i,
      key: 'launchReadiness:messages.productionDemoDataDisabled.passed',
    },
    {
      re: /^Review permission cleanup preview before downgrade apply\.?$/i,
      key: 'launchReadiness:remediations.noReadyPermissionCleanupBlocker',
    },
    {
      re: /^Pending unpaid upgrade$/i,
      key: 'launchReadiness:checks.noPendingUpgradeUnpaidBlocker',
    },
    {
      re: /^No pending unpaid upgrade\.?$/i,
      key: 'launchReadiness:messages.noPendingUpgradeUnpaidBlocker.passed',
    },
    {
      re: /^Due changes not applied$/i,
      key: 'launchReadiness:checks.dueChangesNotApplied',
    },
    {
      re: /^Recycle bin lifecycle does not block tenant launch\.?$/i,
      key: 'launchReadiness:messages.recycleBinNotBlocking.passed',
    },
    {
      re: /^Add setup profile definitions for enabled modules\.?$/i,
      key: 'launchReadiness:remediations.addSetupProfiles',
    },
    {
      re: /^Run verify connection from provisioning tab\.?$/i,
      key: 'launchReadiness:remediations.runVerifyConnection',
    },
    {
      re: /^Run MafertApp migration audit to populate artifacts\.?$/i,
      key: 'launchReadiness:remediations.runMigrationAudit',
    },
    {
      re: /^Build tenant schema plan from approved artifacts\.?$/i,
      key: 'launchReadiness:remediations.buildSchemaPlan',
    },
    {
      re: /^Resolve schema plan blockers before migration\.?$/i,
      key: 'launchReadiness:remediations.resolveSchemaPlanBlockers',
    },
    {
      re: /^Approve tenant schema plan before apply\.?$/i,
      key: 'launchReadiness:remediations.approveSchemaPlan',
    },
    {
      re: /^Enable schema runner for apply\.?$/i,
      key: 'launchReadiness:remediations.enableSchemaRunner',
    },
    {
      re: /^Complete setup wizard steps when MafertApp integration is live\.?$/i,
      key: 'launchReadiness:remediations.completeSetupWizard',
    },
    {
      re: /^Use setup wizard for manual tracking until seed runner is live\.?$/i,
      key: 'launchReadiness:remediations.useSetupWizardTracking',
    },
    {
      re: /^Design support permissions before MafertApp integration\.?$/i,
      key: 'launchReadiness:remediations.designSupportPermissions',
    },
    {
      re: /^Import mafertapp permission module map\.?$/i,
      key: 'launchReadiness:remediations.importPermissionModuleMap',
    },
    {
      re: /^Permission mappings ready$/i,
      key: 'launchReadiness:checks.permissionMappingsReady',
    },
    {
      re: /^Functional capabilities ready$/i,
      key: 'launchReadiness:checks.functionalCapabilitiesReady',
    },
    {
      re: /^MafertApp Functional Capabilities$/i,
      key: 'launchReadiness:checks.functionalCapabilitiesReady',
    },
    {
      re: /^Preview only — MafertApp role cleanup is not applied in this phase\.?$/i,
      key: 'permissionCleanup:previewOnly',
    },
    {
      re: /^Onboarding flow completed for this tenant\.?$/i,
      key: 'systemLabels:readinessMessages.onboardingCompleted',
    },
    {
      re: /^No onboarding\.completed tenant event found\.?$/i,
      key: 'systemLabels:readinessMessages.onboardingMissing',
    },
    {
      re: /^Primary domain configured:\s*(.+)\.?$/i,
      key: 'systemLabels:readinessMessages.primaryDomainConfigured',
      map: (m) => ({ host: m[1].replace(/\.$/, '') }),
    },
    {
      re: /^No primary domain record found\.?$/i,
      key: 'systemLabels:readinessMessages.primaryDomainMissing',
    },
    {
      re: /^Tenant database record exists:\s*(.+)\.?$/i,
      key: 'systemLabels:readinessMessages.tenantDatabaseRecordExists',
      map: (m) => ({ db_name: m[1].replace(/\.$/, '') }),
    },
    {
      re: /^No tenant database record found\.?$/i,
      key: 'systemLabels:readinessMessages.tenantDatabaseRecordMissing',
    },
    {
      re: /^Tenant database record is missing\.?$/i,
      key: 'systemLabels:readinessMessages.tenantDatabaseRecordMissing',
    },
    {
      re: /^Tenant database is ([a-z_]+)\.?$/i,
      key: 'systemLabels:readinessMessages.tenantDatabaseIsStatus',
      map: (m) => ({ status: formatProvisioningStatus(m[1]) || m[1] }),
    },
    {
      re: /^Billing subscription is in good standing\.?$/i,
      key: 'systemLabels:readinessMessages.billingReady',
    },
    {
      re: /^No subscription[ —-]+billing is not configured\.?$/i,
      key: 'systemLabels:readinessMessages.billingNotConfigured',
    },
    {
      re: /^No subscription found for this tenant\.?$/i,
      key: 'systemLabels:readinessMessages.subscriptionMissing',
    },
    {
      re: /^Subscription is payment blocked\.?$/i,
      key: 'systemLabels:readinessMessages.subscriptionPaymentBlocked',
    },
    {
      re: /^Customer admin is linked to customer\.?$/i,
      key: 'systemLabels:readinessMessages.customerAdminLinked',
    },
    {
      re: /^Customer admin is linked but invitation is pending/i,
      key: 'systemLabels:readinessMessages.customerAdminInvitationPending',
    },
    {
      re: /^No customer admin or primary contact linked to customer\.?$/i,
      key: 'systemLabels:readinessMessages.customerAdminMissing',
    },
    {
      re: /^Customer record is missing\.?$/i,
      key: 'systemLabels:readinessMessages.customerMissing',
    },
    {
      re: /^System support enabled/i,
      key: 'systemLabels:readinessMessages.supportReady',
    },
    {
      re: /^Tenant has core\.access feature enabled\.?$/i,
      key: 'systemLabels:readinessMessages.coreAccessEnabled',
    },
    {
      re: /^Cannot evaluate access/i,
      key: 'systemLabels:readinessMessages.cannotEvaluateAccess',
    },
    {
      re: /^S3 prefix recorded/i,
      key: 'systemLabels:readinessMessages.s3PrefixRecorded',
    },
    {
      re: /^MafertApp integration deferred/i,
      key: 'systemLabels:readinessMessages.mafertappIntegrationDeferred',
    },
    {
      re: /^Tenant setup session exists\.?$/i,
      key: 'systemLabels:readinessMessages.setupSessionExists',
    },
    {
      re: /^No tenant setup session found\.?$/i,
      key: 'systemLabels:readinessMessages.setupSessionMissing',
    },
    {
      re: /^Setup session missing/i,
      key: 'systemLabels:readinessMessages.setupSessionMissingLaunch',
    },
    {
      re: /^Cannot evaluate setup steps/i,
      key: 'systemLabels:readinessMessages.cannotEvaluateSetupSteps',
    },
    {
      re: /^All required setup steps are complete\.?$/i,
      key: 'systemLabels:readinessMessages.setupStepsComplete',
    },
    {
      re: /^Setup wizard completed\.?$/i,
      key: 'systemLabels:readinessMessages.setupWizardCompleted',
    },
    {
      re: /^Setup wizard is disabled\.?$/i,
      key: 'systemLabels:readinessMessages.setupWizardDisabled',
    },
    {
      re: /^Required setup steps allow launch\.?$/i,
      key: 'systemLabels:readinessMessages.setupCanLaunch',
    },
    {
      re: /^Provisioning run exists\.?$/i,
      key: 'systemLabels:readinessMessages.provisioningRunExists',
    },
    {
      re: /^No provisioning run found yet\.?$/i,
      key: 'systemLabels:readinessMessages.provisioningRunMissing',
    },
    {
      re: /^Tenant provisioning is disabled\.?$/i,
      key: 'systemLabels:readinessMessages.provisioningDisabled',
    },
    {
      re: /^Tenant database name is valid\.?$/i,
      key: 'systemLabels:readinessMessages.databaseNameValid',
    },
    {
      re: /^Tenant database name is invalid\.?$/i,
      key: 'systemLabels:readinessMessages.databaseNameInvalid',
    },
    {
      re: /^Tenant database name is missing\.?$/i,
      key: 'systemLabels:readinessMessages.databaseNameMissing',
    },
    {
      re: /^Tenant database is provisioned\.?$/i,
      key: 'systemLabels:readinessMessages.databaseProvisioned',
    },
    {
      re: /^Tenant database provisioning failed\.?$/i,
      key: 'systemLabels:readinessMessages.databaseProvisioningFailed',
    },
    {
      re: /^Tenant database provisioning status unknown\.?$/i,
      key: 'systemLabels:readinessMessages.databaseProvisioningUnknown',
    },
    {
      re: /^Health checks are disabled\.?$/i,
      key: 'systemLabels:readinessMessages.healthChecksDisabled',
    },
    {
      re: /^Latest health check is OK\.?$/i,
      key: 'systemLabels:readinessMessages.healthCheckOk',
    },
    {
      re: /^Latest health check failed\.?$/i,
      key: 'systemLabels:readinessMessages.healthCheckFailed',
    },
    {
      re: /^Latest health check has warnings\.?$/i,
      key: 'systemLabels:readinessMessages.healthCheckWarnings',
    },
    {
      re: /^No database health check recorded\.?$/i,
      key: 'systemLabels:readinessMessages.healthCheckMissing',
    },
    {
      re: /^Dedicated tenant migration execution not yet implemented\.?$/i,
      key: 'systemLabels:readinessMessages.migrationsNotImplemented',
    },
    {
      re: /^Migration runner enabled but dry_run_only prevents execution\.?$/i,
      key: 'systemLabels:readinessMessages.migrationDryRunOnly',
    },
    {
      re: /^Schema migration runner flag enabled but execution deferred\.?$/i,
      key: 'systemLabels:readinessMessages.schemaRunnerDeferred',
    },
    {
      re: /^Schema migration runner is disabled \(expected in this phase\)\.?$/i,
      key: 'systemLabels:readinessMessages.schemaRunnerDisabledExpected',
    },
    {
      re: /^No manual review items pending\.?$/i,
      key: 'systemLabels:readinessMessages.noManualReviewPending',
    },
    {
      re: /^Schema plan is ready or approved \(dry-run\)\.?$/i,
      key: 'systemLabels:readinessMessages.schemaPlanReadyDryRun',
    },
    {
      re: /^Schema plan is blocked\.?$/i,
      key: 'systemLabels:readinessMessages.schemaPlanBlocked',
    },
    {
      re: /^Schema plan is not ready yet\.?$/i,
      key: 'systemLabels:readinessMessages.schemaPlanNotReady',
    },
    {
      re: /^Tenant schema plan exists\.?$/i,
      key: 'systemLabels:readinessMessages.schemaPlanExists',
    },
    {
      re: /^No tenant schema plan exists yet\.?$/i,
      key: 'systemLabels:readinessMessages.noSchemaPlan',
    },
    {
      re: /^Schema version selected\.?$/i,
      key: 'systemLabels:readinessMessages.schemaVersionSelected',
    },
    {
      re: /^Selected schema version is blocked\.?$/i,
      key: 'systemLabels:readinessMessages.schemaVersionBlocked',
    },
    {
      re: /^Selected schema version is still draft\.?$/i,
      key: 'systemLabels:readinessMessages.schemaVersionDraft',
    },
    {
      re: /^Tenant seed not yet implemented\.?$/i,
      key: 'systemLabels:readinessMessages.seedNotImplemented',
    },
    {
      re: /^Seed profile blueprint is incomplete\.?$/i,
      key: 'systemLabels:readinessMessages.seedBlueprintIncomplete',
    },
    {
      re: /^Seed blueprint ready \((\d+) setup profiles?, apply deferred\)\.?$/i,
      key: 'systemLabels:readinessMessages.seedBlueprintReady',
      map: (m) => ({ count: m[1], profiles_count: m[1] }),
    },
    {
      re: /^Subscription #(\d+) exists \(([^)]+)\)\.?$/i,
      key: 'systemLabels:readinessMessages.subscriptionExistsWithStatus',
      map: (m) => ({
        id: m[1],
        status: formatStatusCode(m[2]) || formatBillingStatus(m[2]) || m[2],
      }),
    },
    {
      re: /^Tenant access is ([a-z0-9_]+)\.?$/i,
      key: 'systemLabels:readinessMessages.tenantAccessIs',
      map: (m) => ({
        status: formatTenantAccessStatus(m[1]) || humanizeCode(m[1]),
        access_status: formatTenantAccessStatus(m[1]) || humanizeCode(m[1]),
      }),
    },
    {
      re: /^Global settings baseline setup profile is defined\.?$/i,
      key: 'systemLabels:readinessMessages.globalSetupProfileDefined',
    },
    {
      re: /^Global settings baseline setup profile is missing\.?$/i,
      key: 'systemLabels:readinessMessages.globalSetupProfileMissing',
    },
    {
      re: /^No enabled module setup profiles found for tenant modules\.?$/i,
      key: 'systemLabels:readinessMessages.moduleSetupProfilesMissing',
    },
    {
      re: /^No required setup items defined in blueprint\.?$/i,
      key: 'systemLabels:readinessMessages.requiredSetupItemsMissing',
    },
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern.re);
    if (match) {
      const vars = pattern.map ? pattern.map(match) : (maybeParams || {});
      if (i18n.exists(pattern.key)) {
        return i18n.t(pattern.key, vars);
      }
    }
  }

  return text.replace(/\b([a-z][a-z0-9]*(?:_[a-z0-9]+)+)\b/gi, (code) => {
    const normalized = String(code).toLowerCase();
    if (i18n.exists(`customerPortal:accessStatus.${normalized}`)) {
      return i18n.t(`customerPortal:accessStatus.${normalized}`);
    }
    if (i18n.exists(`statuses:tenant_access.${normalized}`)) {
      return i18n.t(`statuses:tenant_access.${normalized}`);
    }
    if (i18n.exists(`systemLabels:featureTypes.${normalized}`)) {
      return i18n.t(`systemLabels:featureTypes.${normalized}`);
    }
    // Do not treat arbitrary snake_case tokens as module codes (avoids trial_active noise).
    return code;
  });
}

export function formatReadinessRecommendation(value) {
  if (!value) {
    return '';
  }
  const text = String(value).trim();
  const fromRemediation = translateLaunchReadinessRemediation(text);
  if (fromRemediation) {
    return fromRemediation;
  }
  return formatReadinessMessage(value);
}

export function formatReadinessCheckTitle(check) {
  if (!check) {
    return '';
  }

  const fromKey = translateLaunchReadinessKey(
    check.label_key || check.metadata?.label_key,
  );
  if (fromKey) {
    return fromKey;
  }

  const code = check.check_code || check.key;
  if (code) {
    const camel = String(code).replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    const key = `launchReadiness:checks.${camel}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
    const systemKey = `systemLabels:readinessChecks.${code}`;
    if (i18n.exists(systemKey)) {
      return i18n.t(systemKey);
    }
  }

  const title = check.title || check.label;
  if (title && !looksLikeI18nKey(title)) {
    const mapped = formatReadinessMessage(title);
    if (mapped && mapped !== title) {
      return mapped;
    }
    if (/_/.test(title) && !/\s/.test(title)) {
      return humanizeCode(title);
    }
    return title;
  }

  return code ? humanizeCode(code) : '';
}

export function formatAccessReasonCode(value) {
  const normalized = String(value || '').toLowerCase();
  if (!normalized) {
    return '';
  }
  const key = `launchReadiness:accessReasons.${normalized}`;
  if (i18n.exists(key)) {
    return i18n.t(key);
  }
  return formatAccessResult(normalized) || humanizeCode(value);
}

export function formatCheckStatus(value) {
  return fromNamespace('statuses', 'check', value);
}

export function formatPermissionFeatureLabel(item) {
  if (!item || typeof item !== 'object') {
    return formatUnknownCode(item);
  }

  const localeTitle = item.title_sk || item.title || item.display_name || item.module_customer_name;
  if (localeTitle && !looksLikeI18nKey(localeTitle)) {
    const labelKey = item.label_key || '';
    const leaf = String(labelKey).split('.').pop() || '';
    if (leaf && /section\d+/i.test(leaf)) {
      const section = leaf.match(/section\s*(\d+)/i)?.[1];
      return section
        ? i18n.t('systemLabels:permissionLabels.qualitySection', {
          section,
          defaultValue: `${localeTitle} — ${section}`,
        })
        : localeTitle;
    }
    if (item.label && item.label !== leaf && !/^[a-z0-9_ -]+$/i.test(item.label)) {
      return item.label;
    }
  }

  const raw = item.label_key || item.label || item.permission_view || '';
  const leaf = String(raw).includes('.')
    ? String(raw).split('.').pop()
    : String(raw).replace(/^access\s+/i, '');
  const normalized = String(leaf || '').toLowerCase().replace(/[\s-]+/g, '_');

  const key = `systemLabels:permissionLabels.${normalized}`;
  if (normalized && i18n.exists(key)) {
    return i18n.t(key);
  }

  if (/^otk.*section[_ ]?(\d+)/i.test(normalized)) {
    const section = normalized.match(/section[_ ]?(\d+)/i)?.[1];
    return i18n.t('systemLabels:permissionLabels.qualitySection', {
      section,
      defaultValue: `Quality — section ${section}`,
    });
  }

  if (normalized.startsWith('otk')) {
    return i18n.t('systemLabels:permissionLabels.quality', { defaultValue: 'Quality control' });
  }

  if (normalized.includes('maintenance')) {
    return i18n.t('systemLabels:permissionLabels.maintenance', { defaultValue: 'Maintenance' });
  }

  if (normalized.includes('support')) {
    return i18n.t('systemLabels:permissionLabels.support', { defaultValue: 'Support' });
  }

  if (normalized.includes('company') && normalized.includes('setting')) {
    return i18n.t('systemLabels:permissionLabels.companySettings', { defaultValue: 'Company settings' });
  }

  if (normalized === 'users' || normalized.startsWith('users_')) {
    return i18n.t('systemLabels:permissionLabels.users', { defaultValue: 'Users' });
  }

  if (item.label && !looksLikeI18nKey(item.label)) {
    return item.label;
  }

  return formatUnknownCode(leaf || raw);
}

export function formatSetupStepList(rawSteps) {
  return String(rawSteps || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((code) => formatSetupStepTitle(code) || formatUnknownCode(code))
    .join(', ');
}

export function formatProvisioningStepTitle(stepCode, t) {
  const code = String(stepCode || '');
  const key = `tenantProvisioning:steps.${code}.title`;
  return safeTranslate(t || i18n.t.bind(i18n), key, humanizeCode(code));
}

/** Exact English provisioning event messages → i18n keys (legacy rows without message_key). */
const PROVISIONING_EVENT_MESSAGE_EXACT = {
  'Dry-run database creation simulated.': 'dry_run_database_creation_simulated',
  'Dry-run provisioning completed.': 'dry_run_provisioning_completed',
  'Dry-run provisioning started.': 'dry_run_provisioning_started',
  'Tenant provisioning started.': 'tenant_provisioning_started',
  'Tenant provisioning completed.': 'tenant_provisioning_completed',
  'Migrations skipped.': 'migrations_skipped',
  'Seed skipped.': 'seed_skipped',
  'Database creation skipped — mode is not create_database.': 'database_creation_skipped_mode',
  'Database creation skipped — disabled by configuration.': 'database_creation_skipped_config',
  'Tenant migration runner is disabled. Schema plan dry-run reference only.': 'migration_runner_disabled_schema_plan_reference_only',
};

const PROVISIONING_EVENT_MESSAGE_PATTERNS = [
  {
    re: /^Database (.+) created\.$/i,
    leaf: 'database_created',
    map: (m) => ({ database: m[1] }),
  },
  {
    re: /^Database (.+) verified \(already existed\)\.$/i,
    leaf: 'database_verified_already_existed',
    map: (m) => ({ database: m[1] }),
  },
];

function resolveProvisioningEventI18nKey(rawKey) {
  if (!rawKey) {
    return null;
  }
  const text = String(rawKey).trim();
  if (text.startsWith('tenantProvisioning:')) {
    return text;
  }
  if (text.startsWith('tenantProvisioning.')) {
    return `tenantProvisioning:${text.slice('tenantProvisioning.'.length)}`;
  }
  const leaf = text.includes('.') ? text.split('.').pop() : text;
  return `tenantProvisioning:event_messages.${leaf}`;
}

/**
 * Localize tenant provisioning event messages.
 * Prefer message_key / metadata.message_key + message_params; fall back to known English raw messages.
 */
export function formatProvisioningEventMessage(eventOrMessage) {
  if (!eventOrMessage) {
    return '';
  }

  if (typeof eventOrMessage === 'object') {
    const metadata = (eventOrMessage.metadata && typeof eventOrMessage.metadata === 'object')
      ? eventOrMessage.metadata
      : {};
    const details = (eventOrMessage.details && typeof eventOrMessage.details === 'object')
      ? eventOrMessage.details
      : {};
    const params = {
      ...metadata,
      ...(eventOrMessage.message_params || {}),
      ...(metadata.message_params || {}),
      ...(details.message_params || {}),
    };
    if (params.database == null && params.database_name != null) {
      params.database = params.database_name;
    }
    const rawKey = eventOrMessage.message_key
      || metadata.message_key
      || details.message_key;
    if (rawKey) {
      const i18nKey = resolveProvisioningEventI18nKey(rawKey);
      if (i18nKey && i18n.exists(i18nKey)) {
        return i18n.t(i18nKey, params);
      }
    }
    return formatProvisioningEventMessage(eventOrMessage.message || '');
  }

  const text = String(eventOrMessage).trim();
  if (!text) {
    return '';
  }

  const mappedLeaf = PROVISIONING_EVENT_MESSAGE_EXACT[text];
  if (mappedLeaf) {
    const key = `tenantProvisioning:event_messages.${mappedLeaf}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
  }

  for (let i = 0; i < PROVISIONING_EVENT_MESSAGE_PATTERNS.length; i += 1) {
    const pattern = PROVISIONING_EVENT_MESSAGE_PATTERNS[i];
    const match = text.match(pattern.re);
    if (match) {
      const key = `tenantProvisioning:event_messages.${pattern.leaf}`;
      if (i18n.exists(key)) {
        return i18n.t(key, pattern.map ? pattern.map(match) : {});
      }
    }
  }

  return text;
}

export function formatSetupStepTitle(stepCode, t) {
  const code = String(stepCode || '');
  const key = `setupWizard:steps.${code}.title`;
  return safeTranslate(t || i18n.t.bind(i18n), key, humanizeCode(code));
}

function formatModuleList(raw) {
  return String(raw)
    .split(',')
    .map((part) => formatModuleCode(part.trim()) || part.trim())
    .filter(Boolean)
    .join(', ');
}

export {
  formatModuleSource,
  formatModuleLabel,
  humanizeCode,
  formatAccessGateStatus,
};
